import Dropdown from '@Components/Common/Dropdown';
import { FormLabel } from '@Components/Common/FormComponent';
import LoadingBtn from '@Components/Common/LoadingBtn';
import IconButton from '@Components/IconButton';
import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Space,
  Stack,
  Tag,
  Text,
  Textarea,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { DeleteIcon } from '@tonic-one/react-icons';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CorrelatedRule,
  CorrelatedRuleShallow,
  CorrelatedRuleContent,
  CorrelatedStatementExpression,
  RuleType,
  RuleTypeFilter,
} from '../types';
import RuleContent from './RuleContent';
import { useQuery } from '@tanstack/react-query';
import { useOrgStore } from '@Models/useOrgModel';
import { RiskAIApi } from '@Apis';
import { queryClient } from 'src/QueryClient';
import { useRiskAIExpressionsQuery } from '../query';

const CustomRuleModal = (props: {
  defaultRule?: CorrelatedRuleShallow;
  isOpen: boolean;
  onSave: (rule: CorrelatedRule) => Promise<void>;
  onClose: () => void;
}) => {
  const { isOpen, onSave, onClose, defaultRule } = props;
  const { t } = useTranslation();
  const { orgId } = useOrgStore();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [ruleId] = useState(defaultRule?.raiRuleId ?? '');
  const [ruleName, setRuleName] = useState(defaultRule?.raiRuleName ?? '');
  const [ruleDesc, setRuleDesc] = useState(defaultRule?.raiRuleDesc ?? '');
  const [ruleContent, setRuleContent] = useState<CorrelatedRuleContent[]>([]);
  const isEmailLoop = defaultRule?.raiEmailLoopEnabled;

  const { data: defaultRuleContent, isLoading } = useQuery({
    enabled: !!defaultRule,
    queryKey: ['correlatedRule', orgId, defaultRule?.raiRuleId],
    queryFn: async () => {
      return await RiskAIApi.getRule(orgId, defaultRule?.raiRuleId ?? '');
    },
    select: ({ data }) => data.raiRuleContent,
    meta: {
      errorNotification: {
        title: t('common.apiException.internalError'),
      },
    },
  });

  useEffect(() => {
    if (defaultRuleContent) {
      setRuleContent(defaultRuleContent);
    }
  }, [defaultRuleContent]);

  const validRuleDefinition = useCallback(
    (ruleContent: CorrelatedRuleContent[]) => {
      return (
        ruleContent.length > 0 &&
        ruleContent.every(({ statement }) => statement.every(validExpression))
      );
    },
    []
  );

  const validRuleName = useCallback(() => {
    let error: string | null = null;
    const validName = ruleName !== '' && ruleName.length <= 256;
    if (!validName) {
      error = t('globalSetting.correlatedRules.error.maxNameLength', {
        limit: 256,
      });
    }
    return error;
  }, [ruleName, t]);

  const validRuleDesc = useCallback(() => {
    let error: string | null = null;
    const validDescription = ruleDesc.length <= 512;
    if (!validDescription) {
      error = t('globalSetting.correlatedRules.error.maxDescLength', {
        limit: 512,
      });
    }
    return error;
  }, [ruleDesc.length, t]);

  const handleAddToRule = useCallback(
    (newRuleContent: CorrelatedRuleContent) => {
      setRuleContent([...ruleContent, newRuleContent]);
    },
    [ruleContent]
  );

  const handleDeleteRuleContent = useCallback(
    (index: number) => {
      setRuleContent([...ruleContent.filter((_, i) => i !== index)]);
    },
    [ruleContent]
  );

  const requiredFieldsAreFilled = useMemo(() => {
    return validRuleDefinition(ruleContent) && ruleName !== '';
  }, [ruleContent, ruleName, validRuleDefinition]);

  const handleSubmit = useCallback(async () => {
    // trim all the string fields
    const newRule: CorrelatedRule = {
      raiRuleId: ruleId,
      raiRuleBuiltIn: RuleType.UserDefined,
      raiRuleName: ruleName.trim(),
      raiRuleDesc: ruleDesc.trim(),
      raiRuleContent: ruleContent,
    };
    const ruleNameError = validRuleName();
    const ruleDescError = validRuleDesc();
    const isValid = !ruleNameError && !ruleDescError && requiredFieldsAreFilled;
    if (isValid) {
      await onSave(newRule);
      if (ruleId !== '') {
        queryClient.invalidateQueries({
          queryKey: ['correlatedRule', orgId, ruleId],
        });
      }
    } else {
      if (ruleNameError) {
        window.showNotification({
          titleMsg: ruleNameError,
          severity: 'error',
        });
      }
      if (ruleDescError) {
        window.showNotification({
          titleMsg: ruleDescError,
          severity: 'error',
        });
      }
    }
  }, [
    ruleId,
    ruleName,
    ruleDesc,
    ruleContent,
    validRuleName,
    validRuleDesc,
    requiredFieldsAreFilled,
    onSave,
    orgId,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" closeOnEsc isClosable>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {t('globalSetting.correlatedRules.userDefinedModal.title')}
        </ModalHeader>
        <ModalBody>
          <Stack gap="6x">
            <Stack gap="4x">
              <Text size="lg">
                {t(
                  'globalSetting.correlatedRules.preDefinedModal.basicProperties'
                )}
              </Text>
              <Flex alignItems="center">
                <FormLabel
                  label={t(
                    'globalSetting.correlatedRules.preDefinedModal.name'
                  )}
                  required
                />
                {isEmailLoop ? (
                  <Text>{ruleName}</Text>
                ) : (
                  <Input
                    value={ruleName}
                    onChange={(e) => setRuleName(e.target.value)}
                  />
                )}
              </Flex>
              <Flex>
                <FormLabel
                  label={t(
                    'globalSetting.correlatedRules.preDefinedModal.description'
                  )}
                />
                {isEmailLoop ? (
                  <Text>{ruleDesc}</Text>
                ) : (
                  <Textarea
                    value={ruleDesc}
                    onChange={(e) => setRuleDesc(e.target.value)}
                  />
                )}
              </Flex>
            </Stack>
            {!isEmailLoop && (
              <Stack gap="4x">
                <Box>
                  <Text size="lg">
                    {t(
                      'globalSetting.correlatedRules.userDefinedModal.filterDefinition'
                    )}
                  </Text>
                  <Text mt="1x" color={colorStyle.color.secondary}>
                    {t(
                      'globalSetting.correlatedRules.userDefinedModal.filterDefinitionDesc'
                    )}
                  </Text>
                </Box>
                <StatementBuilder
                  statementNumber={ruleContent.length}
                  onAddToRule={handleAddToRule}
                />
              </Stack>
            )}
            <Stack minHeight={125} gap="4x">
              <Box>
                <Text size="lg">
                  {t(
                    'globalSetting.correlatedRules.preDefinedModal.ruleDefinition'
                  )}
                </Text>
                <Text mt="1x" color={colorStyle.color.secondary}>
                  {t(
                    'globalSetting.correlatedRules.preDefinedModal.ruleDefinitionDesc'
                  )}
                </Text>
              </Box>
              {isLoading && (
                <Skeleton width="100%" height="60px" animation="pulse" />
              )}
              {!isLoading && ruleContent.length === 0 && (
                <Text
                  textAlign="center"
                  my="auto"
                  color={colorStyle.color.secondary}
                >
                  {t('common.noData')}
                </Text>
              )}
              {!isLoading && ruleContent.length > 0 && (
                <Box>
                  <RuleContent
                    content={ruleContent}
                    Item={RuleContentItem}
                    onDelete={isEmailLoop ? undefined : handleDeleteRuleContent}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <LoadingBtn
            variant="primary"
            disabled={!requiredFieldsAreFilled || isEmailLoop}
            onClick={handleSubmit}
          >
            {t('common.action.label.save')}
          </LoadingBtn>
          <Space width="2x" />
          <Button onClick={onClose}>{t('common.button.cancel')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CustomRuleModal;

const RuleContentItem = (props: {
  children: ReactNode;
  onDelete?: () => void;
}) => {
  const { children, onDelete } = props;
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  return (
    <Flex
      alignItems="center"
      py="2x"
      pl="2x"
      pr="1x"
      _hover={{ background: colorStyle.background.selected }}
    >
      {children}
      {onDelete && (
        <Box ml="auto">
          <IconButton onClick={onDelete}>
            <Icon as={DeleteIcon} />
          </IconButton>
        </Box>
      )}
    </Flex>
  );
};

const StatementBuilder = (props: {
  statementNumber: number;
  onAddToRule: (ruleContent: CorrelatedRuleContent) => void;
}) => {
  const { statementNumber, onAddToRule } = props;
  const [MAX_STATEMENT_NUMBER] = useState(30);
  const { t } = useTranslation();
  const {
    definition,
    isValidDefinition,
    canAddNewExpression,
    onExpressionChange,
    onRemoveExpression,
    addEmpty,
    reset,
  } = useStatementDefinition();

  const handleAddToRule = useCallback(() => {
    onAddToRule({ statement: definition });
    reset();
  }, [definition, onAddToRule, reset]);

  return (
    <>
      <Stack gap="2x">
        <Stack gap="2x" mb="2x">
          {definition.map((expression, index) => (
            <StatementExpressionSelector
              key={`filter-statement-${index}`}
              expression={expression}
              statementDefinition={definition}
              operation={
                index === 0
                  ? undefined
                  : t('globalSetting.correlatedRules.preDefinedModal.and')
              }
              isDeleteDisabled={definition.length === 1}
              onExpressionChange={(statement) =>
                onExpressionChange(index, statement)
              }
              onRemove={() => onRemoveExpression(index)}
              onOpenEdit={() => null}
              disabled={statementNumber >= MAX_STATEMENT_NUMBER}
            />
          ))}
        </Stack>
        <Button
          width="max-content"
          onClick={addEmpty}
          disabled={
            !canAddNewExpression || statementNumber >= MAX_STATEMENT_NUMBER
          }
        >
          {t('globalSetting.correlatedRules.userDefinedModal.addSignal')}
        </Button>
        <Divider />
        <Button
          width="max-content"
          onClick={handleAddToRule}
          disabled={
            !isValidDefinition || statementNumber >= MAX_STATEMENT_NUMBER
          }
        >
          {t('globalSetting.correlatedRules.userDefinedModal.addToRule')}
        </Button>
      </Stack>
    </>
  );
};

const StatementExpressionSelector = (props: {
  expression: CorrelatedStatementExpression;
  onExpressionChange: (statement: CorrelatedStatementExpression) => void;
  statementDefinition: CorrelatedStatementExpression[];
  onRemove: () => void;
  onOpenEdit: () => void;
  operation?: string;
  isDeleteDisabled?: boolean;
  disabled?: boolean;
}) => {
  const {
    expression,
    onExpressionChange,
    statementDefinition,
    onRemove,
    // onOpenEdit,
    operation,
    isDeleteDisabled,
    disabled,
  } = props;
  const { t } = useTranslation();
  const [filterType, setFilterType] = useState<RuleTypeFilter>(
    RuleTypeFilter.All
  );
  const [expSearch, setExpSearch] = useState('');

  const { data: expressions } = useRiskAIExpressionsQuery();

  const expressionsByTypeMap = useMemo(() => {
    if (!expressions) return {};
    const exps = expressions
      .filter((expression) => {
        switch (filterType) {
          case RuleTypeFilter.UserDefined:
            return expression.raiExpBuiltIn === RuleType.UserDefined;
          case RuleTypeFilter.PreDefined:
            return expression.raiExpBuiltIn === RuleType.PreDefined;
          case RuleTypeFilter.All:
            return true;
          default:
            return false;
        }
      })
      .sort((a, b) => a.raiExpName?.localeCompare(b.raiExpName));
    return Object.fromEntries(
      exps.map((exp) => [exp.raiExpId, exp.raiExpName ?? ''])
    );
  }, [expressions, filterType]);

  const expressionOptions = useMemo(() => {
    // keep expressions that are not in the statement definition
    // and are not the current expression
    const expsAvailable = Object.keys(expressionsByTypeMap).filter(
      (expId) =>
        !statementDefinition.some(
          (statement) => statement.raiExpId === expId
        ) && expId !== expression.raiExpId
    );
    // apply search filter
    if (expSearch.trim() === '') return expsAvailable;
    return expsAvailable.filter((expId) =>
      expressionsByTypeMap[expId]
        .toLowerCase()
        .includes(expSearch.toLowerCase())
    );
  }, [
    expSearch,
    expression.raiExpId,
    expressionsByTypeMap,
    statementDefinition,
  ]);

  return (
    <Flex columnGap="2x" alignItems="center">
      {operation && (
        <Tag width="max-content" height="max-content" size="md">
          {operation}
        </Tag>
      )}
      <Dropdown
        value={filterType}
        options={[
          RuleTypeFilter.All,
          RuleTypeFilter.UserDefined,
          RuleTypeFilter.PreDefined,
        ]}
        renderOption={(option) => {
          return (
            {
              [RuleTypeFilter.All]: t(
                'globalSetting.correlatedRules.userDefinedModal.filterType.all'
              ),
              [RuleTypeFilter.UserDefined]: t(
                'globalSetting.correlatedRules.userDefinedModal.filterType.userDefined'
              ),
              [RuleTypeFilter.PreDefined]: t(
                'globalSetting.correlatedRules.userDefinedModal.filterType.preDefined'
              ),
            }[option] || ''
          );
        }}
        onChange={(value) => setFilterType(value)}
        width={operation ? 232 - 48 : 282 - 48}
        disabled={disabled}
      />
      <Dropdown
        value={expression.raiExpId}
        placeholder={t(
          'globalSetting.correlatedRules.userDefinedModal.filterSelect.placeholder'
        )}
        options={expressionOptions}
        renderOption={(option) => expressionsByTypeMap[option] || ''}
        onChange={(expId) => {
          const exp = expressions?.find((exp) => exp.raiExpId === expId);
          if (exp) {
            onExpressionChange({
              raiExpId: exp.raiExpId,
              raiExpBuiltIn: exp.raiExpBuiltIn,
            });
          }
        }}
        minWidth={416 - 8}
        // minWidth={416 - 48}
        flex="1"
        menuHeight={250}
        disabled={disabled}
        placeholderAsValue
        onSearch={setExpSearch}
      />
      {/* <IconButton mx="1x" onClick={onOpenEdit} disabled={isInvalid}>
        <Icon icon="detail" />
      </IconButton> */}
      <IconButton
        mx="1x"
        onClick={onRemove}
        disabled={isDeleteDisabled || disabled}
      >
        <Icon as={DeleteIcon} />
      </IconButton>
    </Flex>
  );
};

const useStatementDefinition = () => {
  const [MAX_EXPRESSIONS] = useState(30);

  const getEmptyExpression = useCallback(
    () =>
      ({
        raiExpId: '',
        raiExpBuiltIn: RuleType.PreDefined,
      }) satisfies CorrelatedStatementExpression,
    []
  );

  const [definition, setDefinition] = useState<CorrelatedStatementExpression[]>(
    [getEmptyExpression()]
  );

  const isValidDefinition = useMemo(
    () =>
      definition.every(validExpression) &&
      definition.length > 0 &&
      definition.length <= MAX_EXPRESSIONS,
    [definition, MAX_EXPRESSIONS]
  );

  const canAddNewExpression = useMemo(
    () => definition.length < MAX_EXPRESSIONS,
    [definition, MAX_EXPRESSIONS]
  );

  const addEmpty = useCallback(() => {
    setDefinition((prev) => [...prev, getEmptyExpression()]);
  }, [getEmptyExpression]);

  const onExpressionChange = useCallback(
    (index: number, newExpression: CorrelatedStatementExpression) => {
      setDefinition((prev) =>
        prev.map((s, i) => (i === index ? { ...s, ...newExpression } : s))
      );
    },
    []
  );

  const onRemoveExpression = useCallback((index: number) => {
    setDefinition((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const reset = useCallback(() => {
    setDefinition([getEmptyExpression()]);
  }, [getEmptyExpression]);

  return {
    canAddNewExpression,
    definition,
    isValidDefinition,
    onExpressionChange,
    onRemoveExpression,
    addEmpty,
    reset,
  };
};

const validExpression = (expression: CorrelatedStatementExpression) => {
  return expression.raiExpId !== '';
};
